import * as React from 'react';
import { AGENT_DEPLOY_MODE, AGENT_ROLE, DEFAULT_TIMEZONE } from '@config/index';
import { BasePageModel, basePageToLocal } from './base';
import {
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  Connection,
  intentInfoToLocal,
  intentInfoToServer,
  compositeInfoToLocal,
  compositeInfoToServer,
  entityInfoToLocal,
  entityInfoToServer,
  connectionToLocal,
  connectionToServer,
  OriginKey,
  originKeyToLocal,
  originKeyToServer,
  TestCase,
  testCaseToLocal,
  testCaseToServer,
  ServiceInfo,
  serviceInfoToLocal,
  serviceInfoToServer,
  ProviderType,
  OrgInfo
} from './index';


// export interface LLMModel {
//   id?: string;
//   url?: string;
//   family?: string;
//   label?: string;
//   apiKey?: string;
// }

// export function llmModelToLocal(data: any): LLMModel {
//   return {
//     id: data.id,
//     url: data.url,
//     family: data.family,
//     label: data.label,
//     apiKey: data.api_key
//   };
// }

// export function llmModelToServer(data: LLMModel): any {
//   return {
//     id: data.id,
//     url: data.url,
//     family: data.family,
//     label: data.label,
//     api_key: data.apiKey
//   };
// }

export interface AgentInfo extends BasePageModel {
  iconUrl: string;
  language?: string;
  isService: boolean;
  isInterface: boolean;
  isServiceImplementation?: boolean;
  mode?: string;
  role?: string;
  privacy?: string;
  prApprovalRequired: boolean;
  backendProviderType: ProviderType;
  template?: OriginKey;
  deployMode?: AGENT_DEPLOY_MODE;
  region?: string;
  commitNumber?: string;
  latestCommitNumber?: string;
  orgId?: string;
  orgName?: string;
  description?: string;
  userId?: string;
  intents: IntentInfo[];
  composites: CompositeInfo[];
  entities: EntityInfo[];
  services: ServiceInfo[];
  connections: Connection[];
  importedAgents: AgentInfo[];
  timeZone?: string;
  readMe?: string;
  testCases: TestCase[];
  masterSelf?: AgentInfo;

  starCount?: number;
  starred?: boolean;

  knowledgeModel?: string;
  knowledgeURL?: string;
  knowledgeKey?: string;
  knowledgeModelName?: string;
  knowledgeSystemPrompt?: string;
  // llmModels?: LLMModel[];

  isWrite?: boolean;

  // for local useage
  oriCache?: AgentInfo;
}

export interface AgentContextProps {
  agent?: AgentInfo;
  handleAgent: (agent?: AgentInfo, org?: OrgInfo) => void;
}

export const AgentContext = React.createContext<AgentContextProps>({
  handleAgent: (agent?: AgentInfo, org?: OrgInfo) => {
    // do nothing
  }
});

export interface AgentMember {
  userId: string;
  nickname: string;
  phone: string;
  memberType: string;
  role: AGENT_ROLE;
}

export const AgentMemberToLacal = (data: any) => ({
  userId: data.person.user_id,
  nickname: data.person.nickname,
  phone: data.phone,
  memberType: data.type,
  role: data.permission
});

export function agentToLocal(data: any): AgentInfo {

  return {
    ...basePageToLocal(data),
    template: data.template && originKeyToLocal(data.template),
    userId: data.user_id,
    mode: data.mode,
    isService: data.is_service,
    isInterface: data.is_interface,
    isServiceImplementation: data.is_service_implementation,
    language: data.lang,
    role: data.permission,
    orgId: data.org_id,
    orgName: data.org_name,
    commitNumber: data.commit_number,
    latestCommitNumber: data.latest_commit_number,
    description: data.description,
    iconUrl: data.icon_url,
    privacy: data.privacy,
    prApprovalRequired: data.pr_approval_required,
    backendProviderType: data.backend_provider_type,
    deployMode: data.deploy_mode,
    region: data.region,
    importedAgents: data.imported_agents && data.imported_agents.map(agentToLocal),
    timeZone: data.time_zone,
    readMe: data.read_me,
    intents: data.intents && data.intents.map(intentInfoToLocal),
    composites: data.composites && data.composites.map(compositeInfoToLocal),
    entities: data.entities && data.entities.map(entityInfoToLocal),
    services: data.services && data.services.map(serviceInfoToLocal),
    connections: data.connections && data.connections.map(connectionToLocal),
    testCases: data.test_cases && data.test_cases.map(testCaseToLocal),
    starCount: data.star_count,
    starred: data.starred,
    knowledgeModel: data.knowledge_model,
    knowledgeURL: data.knowledge_url,
    knowledgeKey: data.knowledge_key,
    knowledgeModelName: data.knowledge_model_name,
    knowledgeSystemPrompt: data.knowledge_system_prompt,
    llmModels: data.llm_models && data.llm_models.map(llmModelToLocal),

    masterSelf: data.master_self && agentToLocal(data.master_self),
    isWrite: data.is_write
  };
}

export function agentToServer(data: AgentInfo): any {

  return {
    id: data.id,
    name: data.name,
    template: data.template && originKeyToServer(data.template),
    is_interface: data.isInterface,
    is_service: data.isService,
    is_service_implementation: data.isServiceImplementation,
    user_id: data.userId,
    mode: data.mode,
    lang: data.language,
    permission: data.role,
    org_name: data.orgName,
    commit_number: data.commitNumber,
    latest_commit_number: data.latestCommitNumber,
    description: data.description,
    icon_url: data.iconUrl,
    privacy: data.privacy,
    pr_approval_required: data.prApprovalRequired,
    backend_provider_type: data.backendProviderType,
    deploy_mode: data.deployMode,
    region: data.region,
    imported_agents: data.importedAgents && data.importedAgents.map(agentToServer),
    time_zone: data.timeZone,
    read_me: data.readMe,
    intents: data.intents && data.intents.map(intentInfoToServer),
    composites: data.composites && data.composites.map(compositeInfoToServer),
    entities: data.entities && data.entities.map(entityInfoToServer),
    services: data.services && data.services.map(serviceInfoToServer),
    connections: data.connections && data.connections.map(connectionToServer),
    test_cases: data.testCases && data.testCases.map(testCaseToServer),
    knowledge_model: data.knowledgeModel,
    knowledge_url: data.knowledgeURL,
    knowledge_key: data.knowledgeKey,
    knowledge_model_name: data.knowledgeModelName,
    knowledge_system_prompt: data.knowledgeSystemPrompt,
    llm_models: data.llmModels && data.llmModels.map(llmModelToServer)
  };
}

export function mergeAgentGeneralInfo(agent: AgentInfo, general: AgentInfo): AgentInfo {
  agent.iconUrl = general.iconUrl;
  agent.description = general.description;
  agent.timeZone = general.timeZone;
  agent.deployMode = general.deployMode;
  agent.privacy = general.privacy;
  agent.prApprovalRequired = general.prApprovalRequired;
  agent.knowledgeKey = general.knowledgeKey;
  agent.knowledgeModel = general.knowledgeModel;
  agent.knowledgeURL = general.knowledgeURL;
  agent.knowledgeModelName = general.knowledgeModelName;
  agent.knowledgeSystemPrompt = general.knowledgeSystemPrompt;
  agent.llmModels = general.llmModels;

  return agent;
}

export interface StarInfo {
  starCount: number;
  starred: boolean;
}

export function starInfoToLocal(res: any): StarInfo {

  return {
    starCount: res.star_count,
    starred: res.starred,

  };
}
